import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getNewsById, getRelatedNews } from "@/utils/movieData";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionTitle from "@/components/feature/SectionTitle";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const news = getNewsById(id);

  useDocumentMeta({
    title: news ? `${news.title} - 秋霞电影影视资讯` : "资讯详情 - 秋霞电影",
    description: news ? news.summary : "秋霞电影影视资讯详情页",
    keywords: news
      ? `${news.title},电影资讯,影视新闻,秋霞电影`
      : "秋霞电影,影视资讯",
  });

  if (!news) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
          <i className="ri-file-unknow-line"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">资讯不存在或已删除</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的资讯可能已被移除，返回资讯列表查看更多内容。</p>
        <Link to="/news" className="mt-6 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = getRelatedNews(news, 3);

  return (
    <div className="bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视资讯", to: "/news" },
            { label: news.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <span className="inline-block rounded bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">{news.category}</span>
            <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {news.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                {news.publishTime}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-newspaper-line"></i></span>
                来源：{news.source}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>
                {news.author}
              </span>
            </div>

            <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <div className="aspect-[16/9] w-full">
                <img src={news.cover} alt={`${news.title} 配图`} title={`${news.title} - 秋霞电影资讯`} className="h-full w-full object-cover" />
              </div>
            </div>

            <div className="mt-6 space-y-5">
              <p className="rounded-lg bg-background-100 p-5 text-sm leading-7 text-foreground-700 md:text-[15px]">
                {news.summary}
              </p>
              {news.content.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-8 text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
              <p className="text-xs text-foreground-500">本文由秋霞电影资讯中心整理发布，内容仅供参考。</p>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                返回资讯列表
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="sticky top-28">
              <SectionTitle title="相关文章推荐" eyebrow="延伸阅读" icon={<i className="ri-links-line"></i>} />
              <div className="space-y-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/news/detail/${r.id}`}
                    className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <div className="h-[70px] w-[100px] shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img src={r.cover} alt={`${r.title} 配图`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                        {r.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-foreground-500">{r.publishTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
              <Link
                to="/news"
                className="mt-5 flex items-center justify-center gap-1.5 rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览更多资讯
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}